"use client";

import React, { useMemo, useState } from "react";

import {
  collectServiceOfferingIndustrySuggestions,
  createEmptyServiceOfferingEntry,
  filterServiceOfferingEditorEntriesByIndustry,
  formatTurnaroundRangeLabel,
  parseTurnaroundRangeLabel,
  splitServiceOfferingFreeTextList,
  type ServiceOfferingEditorEntry,
  type ServiceOfferingsEditorDraft,
} from "./profileServicesPricingEditor";
import {
  formatOfferingPriceLabel,
  type ServicesPricingOfferingDisplay,
} from "./profileServicesPricingDisplay";
import {
  getServicesPricingOfferingIconPresentation,
  inferServicesPricingOfferingIconKey,
  isServicesPricingOfferingIconKey,
  type ServicesPricingOfferingIconKey,
} from "./profileServicesPricingOfferingIcons";
import { ProfileDisclosureChevron } from "./ProfileDisclosureChevron";
import { ServicesPricingOfferingIcon } from "./ServicesPricingOfferingIcon";
import { ProfileMasterDetailEditor } from "./ProfileMasterDetailEditor";

const REVIEWER_AVATAR_COLORS = [
  { bg: "#DBEAFE", text: "#1E40AF" },
  { bg: "#FEE2E2", text: "#991B1B" },
  { bg: "#D1FAE5", text: "#065F46" },
  { bg: "#FED7AA", text: "#9A3412" },
] as const;

export type ProfileServiceOfferingsEditorLabels = Readonly<{
  addLabel: string;
  removeLabel: string;
  titleField: string;
  priceField: string;
  turnaroundField: string;
  tagField: string;
  descriptionField: string;
  deliverablesField: string;
  industriesField: string;
  industriesPlaceholder: string;
  industriesAddLabel: string;
  industriesSuggestionsLabel: string;
  industriesEmptyHint: string;
  previewLabel: string;
  previewExpandLabel: string;
  previewCollapseLabel: string;
  previewDeliverablesTitle: string;
  previewIndustriesLabel: string;
  previewTagsLabel: string;
  previewReviewedByLabel: string;
  previewReviewsLabel: (count: number) => string;
  previewTimesSoldLabel: (count: number) => string;
  emptyStateLabel: string;
  removeIndustryAriaLabel: (industry: string) => string;
  offeringsFromLabel: string;
}>;

export type ProfileServiceOfferingsEditorContentProps = Readonly<{
  componentInstance?: string;
  draft: ServiceOfferingsEditorDraft;
  labels: ProfileServiceOfferingsEditorLabels;
  onChange: (draft: ServiceOfferingsEditorDraft) => void;
  selectedOfferingId: string | null;
  onSelectOfferingId: (id: string | null) => void;
  /**
   * Active industry filter for the sidebar list. When equal to
   * `industryFilterAllLabel` (or unset), all offerings are shown. Only the
   * sidebar list is filtered; the selected offering stays editable.
   */
  industryFilter?: string;
  industryFilterAllLabel?: string;
  /**
   * Optional filter row rendered above the master-detail columns. The chip group
   * itself lives in the consuming app (react-web), so it is passed in as a node.
   */
  filters?: React.ReactNode;
}>;

function resolveOfferingIconKey(entry: ServiceOfferingEditorEntry): ServicesPricingOfferingIconKey {
  return entry.iconKey && isServicesPricingOfferingIconKey(entry.iconKey)
    ? entry.iconKey
    : inferServicesPricingOfferingIconKey({
      serviceCategory: entry.serviceCategory,
      serviceTitle: entry.title,
      serviceTagLabels: entry.serviceTagLabels,
    });
}

function buildOfferingPreview(entry: ServiceOfferingEditorEntry, fromLabel: string): ServicesPricingOfferingDisplay {
  const iconKey = resolveOfferingIconKey(entry);
  const presentation = getServicesPricingOfferingIconPresentation(iconKey);
  return {
    id: entry.id,
    title: entry.title,
    description: entry.description,
    tagLabel: entry.serviceTagLabels[0],
    deliverables: [...entry.deliverables],
    industries: [...entry.targetIndustryLabels],
    priceLabel: formatOfferingPriceLabel({
      pricingModel: entry.pricingModel,
      priceMinCents: entry.priceMinCents,
      priceMaxCents: entry.priceMaxCents,
      currency: entry.currency,
      fromLabel,
    }),
    priceMinCents: entry.priceMinCents,
    turnaroundLabel: formatTurnaroundRangeLabel(entry.turnaroundDaysMin, entry.turnaroundDaysMax),
    iconKey,
    visualTone: presentation.tone,
    purchaseCount: entry.purchaseCount,
    averageRating: entry.averageRating,
    reviewCount: entry.reviewCount,
    featuredReviewerInitials: entry.featuredReviewerInitials,
  };
}

function StarRatingPreview({ rating }: Readonly<{ rating: number }>) {
  const clamped = Math.max(0, Math.min(5, rating));
  return (
    <span className="profile-services-pricing-offerings-editor__stars" aria-hidden="true">
      {Array.from({ length: 5 }, (_, index) => (
        <span
          key={index}
          className={
            index < Math.floor(clamped)
              ? "profile-services-pricing-offerings-editor__star profile-services-pricing-offerings-editor__star--filled"
              : "profile-services-pricing-offerings-editor__star"
          }
        >
          ★
        </span>
      ))}
    </span>
  );
}

function RemoveOfferingIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
      <path d="M3 6h18" strokeLinecap="round" />
      <path d="M8 6V4.5A1.5 1.5 0 0 1 9.5 3h5A1.5 1.5 0 0 1 16 4.5V6" strokeLinecap="round" />
      <path d="M19 6l-.8 14.1A1.9 1.9 0 0 1 16.3 22H7.7a1.9 1.9 0 0 1-1.9-1.9L5 6" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M10 11v6M14 11v6" strokeLinecap="round" />
    </svg>
  );
}

export function ProfileServiceOfferingsEditorContent({
  componentInstance = "profileServiceOfferingsEditor",
  draft,
  labels,
  onChange,
  selectedOfferingId,
  onSelectOfferingId,
  industryFilter,
  industryFilterAllLabel,
  filters,
}: ProfileServiceOfferingsEditorContentProps) {
  const [customIndustry, setCustomIndustry] = useState("");
  const [previewExpanded, setPreviewExpanded] = useState(false);

  const selectedOffering = draft.entries.find((entry) => entry.id === selectedOfferingId)
    ?? draft.entries[0]
    ?? null;

  const visibleEntries = useMemo(
    () => (industryFilter && industryFilterAllLabel
      ? filterServiceOfferingEditorEntriesByIndustry(draft.entries, industryFilter, industryFilterAllLabel)
      : draft.entries),
    [draft.entries, industryFilter, industryFilterAllLabel],
  );

  const industrySuggestions = useMemo(
    () => collectServiceOfferingIndustrySuggestions(draft.entries),
    [draft.entries],
  );

  const updateEntries = (entries: readonly ServiceOfferingEditorEntry[]) => {
    onChange({ entries });
  };

  const updateOffering = (nextEntry: ServiceOfferingEditorEntry) => {
    updateEntries(draft.entries.map((entry) => (entry.id === nextEntry.id ? nextEntry : entry)));
  };

  const addOffering = () => {
    const nextEntry = createEmptyServiceOfferingEntry(draft.entries.length + 1);
    updateEntries([...draft.entries, nextEntry]);
    onSelectOfferingId(nextEntry.id);
    setPreviewExpanded(false);
  };

  const removeOffering = (offeringId: string) => {
    const nextEntries = draft.entries.filter((entry) => entry.id !== offeringId);
    updateEntries(nextEntries);
    if (selectedOfferingId === offeringId) {
      onSelectOfferingId(nextEntries[0]?.id ?? null);
    }
  };

  const addIndustryToOffering = (industry: string) => {
    if (!selectedOffering) return;
    const nextIndustry = industry.trim();
    if (!nextIndustry) return;
    const alreadySelected = selectedOffering.targetIndustryLabels.some(
      (item) => item.toLowerCase() === nextIndustry.toLowerCase(),
    );
    if (alreadySelected) {
      setCustomIndustry("");
      return;
    }
    updateOffering({
      ...selectedOffering,
      targetIndustryLabels: [...selectedOffering.targetIndustryLabels, nextIndustry],
    });
    setCustomIndustry("");
  };

  const removeIndustryFromOffering = (industry: string) => {
    if (!selectedOffering) return;
    updateOffering({
      ...selectedOffering,
      targetIndustryLabels: selectedOffering.targetIndustryLabels.filter((item) => item !== industry),
    });
  };

  const preview = selectedOffering ? buildOfferingPreview(selectedOffering, labels.offeringsFromLabel) : null;

  return (
    <ProfileMasterDetailEditor
      componentInstance={componentInstance}
      filters={filters}
      addLabel={labels.addLabel}
      onAdd={addOffering}
      selectedId={selectedOffering?.id ?? null}
      onSelect={(id) => {
        onSelectOfferingId(id);
        setPreviewExpanded(false);
      }}
      entries={visibleEntries.map((entry) => {
        const iconKey = resolveOfferingIconKey(entry);
        const presentation = getServicesPricingOfferingIconPresentation(iconKey);
        return {
          id: entry.id,
          title: entry.title,
          subtitle: entry.serviceTagLabels.join(", ") || labels.tagField,
          ariaLabel: entry.title,
          leading: (
            <span
              className="dj-selection-card__icon"
              style={{ backgroundColor: presentation.bg }}
            >
              {isServicesPricingOfferingIconKey(iconKey) ? (
                <ServicesPricingOfferingIcon iconKey={iconKey} />
              ) : null}
            </span>
          ),
        };
      })}
    >
      {!selectedOffering ? (
        <div className="profile-master-detail-editor__empty">
          {labels.emptyStateLabel}
        </div>
      ) : (
        <div className="profile-services-pricing-offerings-editor__form">
            <div className="profile-services-pricing-offerings-editor__grid profile-services-pricing-offerings-editor__grid--two">
              <label className="profile-services-pricing-offerings-editor__field">
                <span>{labels.titleField}</span>
                <input
                  value={selectedOffering.title}
                  onChange={(event) => {
                    const title = event.target.value;
                    updateOffering({
                      ...selectedOffering,
                      title,
                      serviceKey: selectedOffering.serviceKey || title.toLowerCase().replace(/[^a-z0-9]+/g, "_"),
                      iconKey: inferServicesPricingOfferingIconKey({
                        serviceCategory: selectedOffering.serviceCategory,
                        serviceTitle: title,
                        serviceTagLabels: selectedOffering.serviceTagLabels,
                      }),
                    });
                  }}
                />
              </label>
              <label className="profile-services-pricing-offerings-editor__field">
                <span>{labels.priceField}</span>
                <input
                  type="number"
                  inputMode="decimal"
                  value={selectedOffering.priceMinCents !== undefined ? String(selectedOffering.priceMinCents / 100) : ""}
                  onChange={(event) => {
                    const parsed = Number.parseFloat(event.target.value);
                    updateOffering({
                      ...selectedOffering,
                      pricingModel: "fixed",
                      currency: "USD",
                      priceMinCents: Number.isFinite(parsed) ? Math.round(parsed * 100) : undefined,
                      priceMaxCents: Number.isFinite(parsed) ? Math.round(parsed * 100) : undefined,
                    });
                  }}
                />
              </label>
              <label className="profile-services-pricing-offerings-editor__field">
                <span>{labels.turnaroundField}</span>
                <input
                  value={formatTurnaroundRangeLabel(selectedOffering.turnaroundDaysMin, selectedOffering.turnaroundDaysMax)}
                  placeholder="1-3 days"
                  onChange={(event) => {
                    const range = parseTurnaroundRangeLabel(event.target.value);
                    updateOffering({
                      ...selectedOffering,
                      turnaroundDaysMin: range.min,
                      turnaroundDaysMax: range.max,
                    });
                  }}
                />
              </label>
              <label className="profile-services-pricing-offerings-editor__field">
                <span>{labels.tagField}</span>
                <input
                  value={selectedOffering.serviceTagLabels.join(", ")}
                  placeholder="Advanced Mapping, Inspection"
                  onChange={(event) => updateOffering({
                    ...selectedOffering,
                    serviceTagLabels: [...splitServiceOfferingFreeTextList(event.target.value)],
                    iconKey: inferServicesPricingOfferingIconKey({
                      serviceCategory: selectedOffering.serviceCategory,
                      serviceTitle: selectedOffering.title,
                      serviceTagLabels: splitServiceOfferingFreeTextList(event.target.value),
                    }),
                  })}
                />
              </label>
            </div>

            <label className="profile-services-pricing-offerings-editor__field">
              <span>{labels.descriptionField}</span>
              <textarea
                rows={3}
                value={selectedOffering.description}
                onChange={(event) => updateOffering({
                  ...selectedOffering,
                  description: event.target.value,
                })}
              />
            </label>

            <div className="profile-services-pricing-offerings-editor__grid profile-services-pricing-offerings-editor__grid--two">
              <label className="profile-services-pricing-offerings-editor__field">
                <span>{labels.deliverablesField}</span>
                <textarea
                  rows={6}
                  placeholder="One deliverable per line"
                  value={selectedOffering.deliverables.join("\n")}
                  onChange={(event) => updateOffering({
                    ...selectedOffering,
                    deliverables: event.target.value.split("\n").map((line) => line.trim()).filter(Boolean),
                  })}
                />
              </label>

              <div className="profile-services-pricing-offerings-editor__field">
                <span>{labels.industriesField}</span>
                <div className="profile-services-pricing-offerings-editor__industries-box">
                  <div className="profile-services-pricing-offerings-editor__industry-chips">
                    {selectedOffering.targetIndustryLabels.map((industry) => (
                      <span key={industry} className="profile-services-pricing-offerings-editor__industry-chip">
                        {industry}
                        <button
                          type="button"
                          aria-label={labels.removeIndustryAriaLabel(industry)}
                          onClick={() => removeIndustryFromOffering(industry)}
                        >
                          ×
                        </button>
                      </span>
                    ))}
                    {selectedOffering.targetIndustryLabels.length === 0 ? (
                      <span className="profile-services-pricing-offerings-editor__industry-empty">
                        {labels.industriesEmptyHint}
                      </span>
                    ) : null}
                  </div>

                  <div className="profile-services-pricing-offerings-editor__industry-add-row">
                    <input
                      value={customIndustry}
                      list={`${componentInstance}-industry-suggestions`}
                      placeholder={labels.industriesPlaceholder}
                      onChange={(event) => setCustomIndustry(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter") {
                          event.preventDefault();
                          addIndustryToOffering(customIndustry);
                        }
                      }}
                    />
                    <datalist id={`${componentInstance}-industry-suggestions`}>
                      {industrySuggestions.map((industry) => (
                        <option key={industry} value={industry} />
                      ))}
                    </datalist>
                    <button
                      type="button"
                      className="profile-services-pricing-offerings-editor__industry-add-button"
                      onClick={() => addIndustryToOffering(customIndustry)}
                    >
                      + {labels.industriesAddLabel}
                    </button>
                  </div>

                  <div className="profile-services-pricing-offerings-editor__industry-suggestions">
                    <span>{labels.industriesSuggestionsLabel}</span>
                    <div className="profile-services-pricing-offerings-editor__suggestion-chips">
                      {industrySuggestions.map((industry) => {
                        const selected = selectedOffering.targetIndustryLabels.some(
                          (item) => item.toLowerCase() === industry.toLowerCase(),
                        );
                        return (
                          <button
                            key={industry}
                            type="button"
                            className={[
                              "profile-services-pricing-offerings-editor__suggestion-chip",
                              selected ? "profile-services-pricing-offerings-editor__suggestion-chip--selected" : "",
                            ].filter(Boolean).join(" ")}
                            onClick={() => (
                              selected
                                ? removeIndustryFromOffering(industry)
                                : addIndustryToOffering(industry)
                            )}
                          >
                            {industry}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {preview ? (
              <div className="profile-services-pricing-offerings-editor__preview">
                <button
                  type="button"
                  className="profile-services-pricing-offerings-editor__preview-toggle"
                  aria-expanded={previewExpanded}
                  onClick={() => setPreviewExpanded((expanded) => !expanded)}
                >
                  <div className="profile-services-pricing-offerings-editor__preview-body">
                    <div className="profile-services-pricing-offerings-editor__preview-header">
                      <strong>{labels.previewLabel}</strong>
                      <span>
                        {previewExpanded ? labels.previewCollapseLabel : labels.previewExpandLabel}
                        <ProfileDisclosureChevron expanded={previewExpanded} />
                      </span>
                    </div>

                    <div className="profile-services-pricing-offerings-editor__preview-summary">
                      <div className="profile-services-pricing-offerings-editor__preview-main">
                        <div className="profile-services-pricing-offerings-editor__preview-title-row">
                          <strong>{preview.title}</strong>
                          {preview.tagLabel ? (
                            <span className="profile-services-pricing-offering__tag">{preview.tagLabel}</span>
                          ) : null}
                        </div>
                        {preview.description ? <p>{preview.description}</p> : null}
                      </div>
                      <div className="profile-services-pricing-offerings-editor__preview-price">
                        <strong>{preview.priceLabel}</strong>
                        {preview.turnaroundLabel ? <span>{preview.turnaroundLabel}</span> : null}
                      </div>
                    </div>

                    {(preview.averageRating !== undefined && preview.averageRating > 0)
                      || (preview.reviewCount !== undefined && preview.reviewCount > 0)
                      || (preview.purchaseCount !== undefined && preview.purchaseCount > 0) ? (
                        <div className="profile-services-pricing-offerings-editor__preview-stats">
                          {preview.averageRating !== undefined && preview.averageRating > 0 ? (
                            <span className="profile-services-pricing-offerings-editor__preview-rating">
                              <StarRatingPreview rating={preview.averageRating} />
                              <span>
                                {preview.averageRating.toFixed(1)}
                                {preview.reviewCount !== undefined && preview.reviewCount > 0
                                  ? ` (${labels.previewReviewsLabel(preview.reviewCount)})`
                                  : ""}
                              </span>
                            </span>
                          ) : null}
                          {preview.purchaseCount !== undefined && preview.purchaseCount > 0 ? (
                            <span>{labels.previewTimesSoldLabel(preview.purchaseCount)}</span>
                          ) : null}
                        </div>
                      ) : null}

                    {preview.featuredReviewerInitials?.length ? (
                      <div className="profile-services-pricing-offerings-editor__preview-reviewers">
                        <span>{labels.previewReviewedByLabel}</span>
                        <span className="profile-services-pricing-offerings-editor__reviewer-initials">
                          {preview.featuredReviewerInitials.slice(0, 4).map((initials, index) => {
                            const color = REVIEWER_AVATAR_COLORS[index % REVIEWER_AVATAR_COLORS.length]!;
                            return (
                              <span
                                key={`${preview.id}-${initials}`}
                                style={{ backgroundColor: color.bg, color: color.text }}
                              >
                                {initials}
                              </span>
                            );
                          })}
                        </span>
                      </div>
                    ) : null}
                  </div>

                  {previewExpanded ? (
                    <div className="profile-services-pricing-offerings-editor__preview-expanded">
                      <strong>{labels.previewDeliverablesTitle}</strong>
                      <ul>
                        {selectedOffering.deliverables.map((deliverable) => (
                          <li key={deliverable}>
                            <span aria-hidden="true">✓</span>
                            {deliverable}
                          </li>
                        ))}
                      </ul>
                      <div className="profile-services-pricing-offerings-editor__preview-meta">
                        <div>
                          <span>{labels.previewIndustriesLabel}</span>
                          {selectedOffering.targetIndustryLabels.map((industry) => (
                            <span key={industry} className="profile-services-pricing-offerings-editor__preview-meta-chip profile-services-pricing-offerings-editor__preview-meta-chip--industry">
                              {industry}
                            </span>
                          ))}
                        </div>
                        <div>
                          <span>{labels.previewTagsLabel}</span>
                          {selectedOffering.serviceTagLabels.map((tag) => (
                            <span key={tag} className="profile-services-pricing-offerings-editor__preview-meta-chip">
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  ) : null}
                </button>
              </div>
            ) : null}

            <div className="profile-services-pricing-offerings-editor__remove-row">
              <button
                type="button"
                className="profile-services-pricing-offerings-editor__remove-button"
                onClick={() => removeOffering(selectedOffering.id)}
              >
                <RemoveOfferingIcon />
                {labels.removeLabel}
              </button>
            </div>
          </div>
        )}
    </ProfileMasterDetailEditor>
  );
}
